import { formatDurationSeconds } from "@app/date";
import {
  type RequestsSortBy,
  type RequestsSortOrder,
  type RequestsStatus,
  fetchLlmKeyRequests,
  fetchLlmModels,
  selectLlmKeyRequestsByKeyId,
} from "@app/deploy";
import { useLoader, useQuery, useSelector } from "@app/react";
import { llmKeyRequestDetailUrl } from "@app/routes";
import { titleCase } from "@app/string-utils";
import { tunaEvent, useTunaHoverEvent } from "@app/tuna";
import type { DeployLlmKeyRequest } from "@app/types";
import { type ZonedDateTime, now } from "@internationalized/date";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Button,
  CopyText,
  Group,
  IconChevronRight,
  InputDateTimeRange,
  Label,
  SearchableSelect,
  Select,
  Tooltip,
  tokens,
} from "../shared";
import { ControlledDataTable } from "../shared/datatable";
import {
  CurrencyCell,
  DateTimeCell,
  PillCell,
} from "../shared/datatable/cells";
import type {
  DataTableColDef,
  DataTableSort,
} from "../shared/datatable/datatable";

const useLlmKeyDetailTunaPayload = (data: Record<string, unknown> = {}) => {
  const { id = "" } = useParams();
  return JSON.stringify({ llmKeyId: id, ...data });
};

const TokensCell = ({ data }: { data: DeployLlmKeyRequest }) => {
  const tunaPayload = useLlmKeyDetailTunaPayload({
    field: "tokens",
    requestId: data.id,
  });
  const tunaHoverProps = useTunaHoverEvent(
    "llm-key-detail-hover-tooltip",
    tunaPayload,
  );
  return (
    <Tooltip
      fluid
      text={
        <div className="grid grid-cols-[6ch_1fr] gap-2">
          <div className="font-semibold">Input</div>
          <div className="text-right">{data.inputTokens.toLocaleString()}</div>
          <div className="font-semibold">Output</div>
          <div className="text-right">{data.outputTokens.toLocaleString()}</div>
        </div>
      }
    >
      <span
        {...tunaHoverProps}
        className="border-b border-dotted border-gray-400"
      >
        {data.totalTokens.toLocaleString()}
      </span>
    </Tooltip>
  );
};

const RequestActionsCell = ({ data }: { data: DeployLlmKeyRequest }) => {
  const { id = "" } = useParams();
  const tunaPayload = useLlmKeyDetailTunaPayload({ requestId: data.id });
  return (
    <div className="flex justify-center items-center p-1 hover:bg-gray-200 rounded w-8 h-8">
      <Link
        to={llmKeyRequestDetailUrl(id, data.id)}
        className="flex justify-center items-center p-1 hover:bg-gray-200 rounded w-8 h-8"
        onClick={() =>
          tunaEvent("llm-key-detail-request-detail-clicked", tunaPayload)
        }
      >
        <IconChevronRight className="w-5 h-5 stroke-black hover:stroke-indigo" />
      </Link>
    </div>
  );
};

// Helper to get default date range (last 7 days)
const getDefaultDateTimeRange = () => {
  const currentTime = now("UTC");
  const startTime = currentTime.subtract({ days: 7 });
  return { start: startTime, end: currentTime };
};

const DateRangeFilter = ({
  value,
  className,
  onChange,
}: {
  value: { start: ZonedDateTime; end: ZonedDateTime };
  className?: string;
  onChange: (
    value: { start: ZonedDateTime; end: ZonedDateTime } | null,
  ) => void;
}) => {
  return (
    <div className={className}>
      <Label htmlFor="date-range-requests">Date & Time Range</Label>
      <InputDateTimeRange
        id="date-range-requests"
        value={value}
        onChange={onChange}
        disableFuture
        useUTC
        maxRangeDays={7}
        quickRanges={[
          { label: "Last hour", duration: "1h" },
          { label: "Last day", duration: "1d" },
          { label: "Last 7 days", duration: "7d" },
        ]}
        className="w-full"
      />
    </div>
  );
};

const StatusFilter = ({
  value,
  className,
  onChange,
}: {
  value: RequestsStatus;
  className?: string;
  onChange: (status: RequestsStatus) => void;
}) => {
  return (
    <div className={className}>
      <Label htmlFor="status-filter">Status</Label>
      <Select
        id="status-filter"
        value={value}
        options={[
          { label: "All", value: "" },
          { label: "Success", value: "success" },
          { label: "Failure", value: "failure" },
        ]}
        onSelect={(option) => onChange(option.value)}
        className="w-full"
      />
    </div>
  );
};

const ModelFilter = ({
  value,
  className,
  onChange,
}: {
  value: string;
  className?: string;
  onChange: (model: string) => void;
}) => {
  useQuery(fetchLlmModels());
  const loader = useLoader(fetchLlmModels);
  const allModels: Array<{ id: string; supported: boolean }> =
    loader.meta?.models || [];

  // Filter to only supported models and convert to SelectOption format
  const modelOptions = useMemo(
    () =>
      allModels
        .filter((m) => m.supported)
        .map((m) => ({
          value: m.id,
          label: m.id,
        })),
    [allModels],
  );

  return (
    <div className={className}>
      <Label htmlFor="model-filter">Model</Label>
      <SearchableSelect
        id="model-filter"
        value={value}
        onChange={(val) => onChange(val || "")}
        options={[{ value: "", label: "All Models" }, ...modelOptions]}
        placeholder="All Models"
        className="w-full"
        searchPlaceholder="Search models..."
      />
    </div>
  );
};

const FilterBar = ({
  onChange,
}: {
  onChange: (filters: {
    startTime: string;
    endTime: string;
    status: RequestsStatus;
    model: string;
  }) => void;
}) => {
  const [dateRange, setDateRange] = useState<{
    start: ZonedDateTime;
    end: ZonedDateTime;
  }>(getDefaultDateTimeRange());
  const [status, setStatus] = useState<RequestsStatus>("");
  const [model, setModel] = useState<string>("");

  const applyFilters = () => {
    onChange({
      startTime: dateRange.start.toAbsoluteString(),
      endTime: dateRange.end.toAbsoluteString(),
      status,
      model,
    });
  };

  const resetFilters = () => {
    const defaultRange = getDefaultDateTimeRange();
    setDateRange(defaultRange);
    setStatus("");
    setModel("");

    onChange({
      startTime: defaultRange.start.toAbsoluteString(),
      endTime: defaultRange.end.toAbsoluteString(),
      status: "",
      model: "",
    });
  };

  const handleDateRangeChange = (
    value: { start: ZonedDateTime; end: ZonedDateTime } | null,
  ) => {
    if (value?.start && value?.end) {
      setDateRange(value as { start: ZonedDateTime; end: ZonedDateTime });
    }
  };

  return (
    <div className="flex items-end gap-2 max-w-full">
      <DateRangeFilter
        className="flex-shrink-0"
        value={dateRange}
        onChange={handleDateRangeChange}
      />
      <StatusFilter className="flex-grow" value={status} onChange={setStatus} />
      <ModelFilter className="flex-grow" value={model} onChange={setModel} />
      <div className="flex items-end gap-2 flex-shrink-0">
        <Button type="button" variant="white" size="md" onClick={resetFilters}>
          Reset
        </Button>
        <Button
          type="button"
          variant="primary"
          size="md"
          onClick={applyFilters}
        >
          Apply Filters
        </Button>
      </div>
    </div>
  );
};

const SORT_FIELD_MAP: Partial<Record<string, RequestsSortBy>> = {
  startTime: "start_time",
  totalTokens: "total_tokens",
  spend: "spend",
};

export const LlmKeyRequestsPage = () => {
  const { id = "" } = useParams();

  // Applied filters for API call
  const defaultRange = getDefaultDateTimeRange();
  const [filters, setFilters] = useState<{
    startTime: string;
    endTime: string;
    status: RequestsStatus;
    model: string;
  }>({
    startTime: defaultRange.start.toAbsoluteString(),
    endTime: defaultRange.end.toAbsoluteString(),
    status: "",
    model: "",
  });

  const [sortBy, setSortBy] = useState<RequestsSortBy>("start_time");
  const [sortOrder, setSortOrder] = useState<RequestsSortOrder>("desc");
  const [page, setPage] = useState(1);
  const nextPage = () => setPage((p) => p + 1);
  const prevPage = () => setPage((p) => p - 1);

  // Fetch data with filters
  const action = useMemo(
    () =>
      fetchLlmKeyRequests({
        id,
        ...filters,
        sortBy: sortBy || undefined,
        sortOrder: sortOrder || undefined,
        page,
        perPage: 30,
      }),
    [id, filters, sortBy, sortOrder, page],
  );

  useQuery(action);
  const loader = useLoader(action);
  const requests = useSelector((s) =>
    selectLlmKeyRequestsByKeyId(s, { llmKeyId: id }),
  );
  const { totalCount, totalPages } = loader.meta || {};

  // Preserve last known pagination counts to avoid showing stale zeros during
  // loading or page transitions.
  const [lastTotalCount, setLastTotalCount] = useState(0);
  const [lastTotalPages, setLastTotalPages] = useState(1);
  useEffect(() => {
    if (totalCount != null) setLastTotalCount(totalCount);
    if (totalPages != null) setLastTotalPages(Math.max(1, totalPages));
  }, [totalCount, totalPages]);

  const handleFilter = useCallback(
    (newFilters: {
      startTime: string;
      endTime: string;
      status: RequestsStatus;
      model: string;
    }) => {
      const changedFilters = (
        Object.keys(newFilters) as (keyof typeof newFilters)[]
      ).filter((key) => filters[key] !== newFilters[key]);

      if (changedFilters.length > 0) {
        tunaEvent(
          "llm-key-detail-filters-changed",
          JSON.stringify({ llmKeyId: id, ...newFilters, changedFilters }),
        );
      }

      setFilters(newFilters);
      setPage(1);
    },
    [id, filters, setFilters, setPage],
  );

  const handleSort = useCallback(
    (sort: DataTableSort | null) => {
      if (!sort) return;
      tunaEvent(
        "llm-key-detail-sort-changed",
        JSON.stringify({
          llmKeyId: id,
          field: sort.field,
          direction: sort.direction,
        }),
      );
      setSortBy(SORT_FIELD_MAP[sort.field] ?? "start_time");
      setSortOrder(sort.direction);
      setPage(1);
    },
    [id],
  );

  const colDefs = useMemo<DataTableColDef<DeployLlmKeyRequest>[]>(
    () => [
      {
        field: "id",
        headerName: "Request ID",
        sortable: false,
        cellRenderer: ({ data }: { data: DeployLlmKeyRequest }) => (
          <CopyText text={data.id}>
            <span className={`${tokens.type.darker} font-mono`}>{data.id}</span>
          </CopyText>
        ),
      },
      {
        field: "model",
        headerName: "Model",
        sortable: false,
        cellRenderer: ({ data }: { data: DeployLlmKeyRequest }) => (
          <CopyText text={data.model}>
            <span className={tokens.type.darker}>{data.model}</span>
          </CopyText>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        sortable: false,
        cellRenderer: ({ data }: { data: DeployLlmKeyRequest }) => (
          <PillCell
            value={data.status}
            label={titleCase(data.status)}
            valueToVariantMap={{
              success: "success",
              failure: "error",
            }}
          />
        ),
      },
      {
        field: "startTime",
        headerName: "Timestamp (UTC)",
        sortable: true,
        initialSort: "desc",
        cellRenderer: ({ data }: { data: DeployLlmKeyRequest }) => (
          <DateTimeCell date={data.startTime} />
        ),
      },
      {
        field: "durationSeconds",
        headerName: "Duration",
        sortable: false,
        cellRenderer: ({ data }: { data: DeployLlmKeyRequest }) => (
          <span className={tokens.type.darker}>
            {formatDurationSeconds(data.durationSeconds)}
          </span>
        ),
      },
      {
        field: "totalTokens",
        headerName: "Tokens",
        sortable: true,
        getTooltip: ({ data }: { data: DeployLlmKeyRequest }) => {
          return (
            <div className="grid grid-cols-[6ch_1fr] gap-2">
              <div className="font-semibold">Input</div>
              <div className="text-right">
                {data.inputTokens.toLocaleString()}
              </div>
            </div>
          );
        },
        cellRenderer: TokensCell,
      },
      {
        field: "spend",
        headerName: "Cost",
        sortable: true,
        cellRenderer: ({ data }: { data: DeployLlmKeyRequest }) => (
          <CurrencyCell value={data.spend} decimalPlaces={4} />
        ),
      },
      {
        colId: "actions",
        sortable: false,
        width: 60,
        resizable: false,
        suppressAutoSize: true,
        suppressSizeToFit: true,
        cellRenderer: RequestActionsCell,
      },
    ],
    [id],
  );

  return (
    <Group>
      <FilterBar onChange={handleFilter} />

      <hr />

      <ControlledDataTable<DeployLlmKeyRequest>
        rowData={requests}
        colDefs={colDefs}
        isLoading={loader.isLoading}
        searchable={false}
        page={page}
        totalPages={lastTotalPages}
        totalItems={lastTotalCount}
        onPrev={prevPage}
        onNext={nextPage}
        onSort={handleSort}
      />
    </Group>
  );
};
